import React, { useState, useEffect, useRef, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  FaMapMarkerAlt,
  FaSearch,
  FaCrosshairs,
  FaExternalLinkAlt,
  FaKeyboard,
  FaMapMarkedAlt,
  FaCheckCircle,
  FaInfoCircle,
  FaTimes,
  FaCopy,
  FaMagic,
} from "react-icons/fa";
import "./LocationPicker.css";

// Helper: Custom SVG Leaflet Pin Icon to avoid Webpack asset loading issues
const createCustomMarkerIcon = () => {
  return L.divIcon({
    className: "soloway-custom-map-marker",
    html: `
      <div class="marker-pin-wrapper">
        <div class="marker-pin-pulse"></div>
        <div class="marker-pin-icon">
          <svg viewBox="0 0 24 24" width="32" height="32" fill="#2563eb" stroke="#ffffff" stroke-width="1.5">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 0 1 0-5 2.5 2.5 0 0 1 0 5z"/>
          </svg>
        </div>
      </div>
    `,
    iconSize: [32, 42],
    iconAnchor: [16, 42],
    popupAnchor: [0, -38],
  });
};

// Helper: Parse coordinates or Google Maps links
export const parseCoordinates = (input) => {
  if (!input || typeof input !== "string") return null;
  const str = input.trim();

  // Pattern 1: Raw numbers "lat, lng" e.g. "6.9271, 79.8612" or "6.9271,79.8612"
  const rawCoordRegex = /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/;
  if (rawCoordRegex.test(str)) {
    const [latStr, lngStr] = str.split(",").map((s) => s.trim());
    const lat = parseFloat(latStr);
    const lng = parseFloat(lngStr);
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng, type: "coordinates" };
    }
  }

  // Pattern 2: Google Maps URL with @lat,lng
  const atMatch = str.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (atMatch) {
    const lat = parseFloat(atMatch[1]);
    const lng = parseFloat(atMatch[2]);
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng, type: "gmaps_url_at" };
    }
  }

  // Pattern 3: Google Maps URL with ?q=lat,lng or query=lat,lng
  const qMatch = str.match(/[?&](?:q|query|ll)=(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (qMatch) {
    const lat = parseFloat(qMatch[1]);
    const lng = parseFloat(qMatch[2]);
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng, type: "gmaps_url_q" };
    }
  }

  return null;
};

// Helper: Generate Google Maps URL for coordinates
export const toGoogleMapsUrl = (coordsOrLink) => {
  if (!coordsOrLink) return "https://maps.google.com";
  const str = String(coordsOrLink).trim();
  if (str.startsWith("http://") || str.startsWith("https://")) {
    return str;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(str)}`;
};

/**
 * LocationPicker Component
 * @param {string} value - Current coordinates or Google Maps link
 * @param {function} onChange - Callback when coordinates/link changes
 * @param {string} address - Current address (optional)
 * @param {function} onAddressChange - Callback to update address from map selection (optional)
 * @param {boolean} required - Whether the field is required
 * @param {string} label - Custom label
 * @param {string} placeholder - Custom placeholder
 * @param {string} helpText - Custom helper text
 * @param {string} id - Unique ID for input
 */
const LocationPicker = ({
  value = "",
  onChange,
  address = "",
  onAddressChange,
  required = false,
  label = "Google Maps Location or Coordinates",
  placeholder = "e.g. 6.9271, 79.8612 or https://maps.app.goo.gl/...",
  helpText = "Choose on map or paste your Google Maps link. This is shared with guests upon booking.",
  id = "location-picker",
}) => {
  // Mode: 'map' | 'manual'
  const [activeMode, setActiveMode] = useState("map");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [reverseLoading, setReverseLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [geoLocating, setGeoLocating] = useState(false);
  const [geoError, setGeoError] = useState("");

  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Default initial position (Center of Sri Lanka: Colombo / Kandy region)
  const defaultPos = useRef({ lat: 6.9271, lng: 79.8612 }).current;

  // Format coordinates to standard string
  const formatCoordString = (lat, lng) => `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

  // Reverse Geocoding with OpenStreetMap Nominatim
  const fetchAddressFromCoords = async (lat, lng) => {
    try {
      setReverseLoading(true);
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        {
          headers: {
            "Accept-Language": "en",
          },
        }
      );
      if (res.ok) {
        const data = await res.json();
        if (data && data.display_name) {
          return data.display_name;
        }
      }
    } catch (err) {
      console.warn("Reverse geocode failed:", err);
    } finally {
      setReverseLoading(false);
    }
    return null;
  };

  // Update selection from map click or drag
  const handleMapLocationSelect = useCallback(
    async (lat, lng, autoFillAddress = false) => {
      const coordStr = formatCoordString(lat, lng);
      if (onChange) {
        onChange(coordStr);
      }

      // Update marker position
      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng]);
        markerRef.current
          .bindPopup(
            `<div class="soloway-map-popup">
            <strong>Selected Location</strong>
            <p>${coordStr}</p>
          </div>`
          )
          .openPopup();
      }

      // If requested or address is empty, suggest address
      if (autoFillAddress || (!address && onAddressChange)) {
        const detectedAddress = await fetchAddressFromCoords(lat, lng);
        if (detectedAddress && onAddressChange) {
          onAddressChange(detectedAddress);
        }
      }
    },
    [onChange, address, onAddressChange]
  );

  // Initialize Map
  useEffect(() => {
    if (!mapContainerRef.current) return;

    if (mapInstanceRef.current) {
      mapInstanceRef.current.invalidateSize();
      return;
    }

    const parsedInit = parseCoordinates(value);
    const initLat = parsedInit ? parsedInit.lat : defaultPos.lat;
    const initLng = parsedInit ? parsedInit.lng : defaultPos.lng;
    const initZoom = parsedInit ? 14 : 9;

    const map = L.map(mapContainerRef.current, {
      center: [initLat, initLng],
      zoom: initZoom,
      zoomControl: true,
      scrollWheelZoom: true,
    });

    // Clean OpenStreetMap Tile Layer
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    // Custom Draggable Marker
    const marker = L.marker([initLat, initLng], {
      icon: createCustomMarkerIcon(),
      draggable: true,
    }).addTo(map);

    if (parsedInit) {
      marker.bindPopup(
        `<div class="soloway-map-popup">
          <strong>Selected Location</strong>
          <p>${formatCoordString(initLat, initLng)}</p>
        </div>`
      );
    }

    // Drag end listener
    marker.on("dragend", (e) => {
      const { lat, lng } = e.target.getLatLng();
      handleMapLocationSelect(lat, lng, false);
    });

    // Map click listener
    map.on("click", (e) => {
      const { lat, lng } = e.latlng;
      handleMapLocationSelect(lat, lng, false);
    });

    mapInstanceRef.current = map;
    markerRef.current = marker;

    // Fix map rendering when initialized
    setTimeout(() => {
      map.invalidateSize();
    }, 200);

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      markerRef.current = null;
    };
  }, [defaultPos.lat, defaultPos.lng, handleMapLocationSelect, value]);

  // Sync map center & marker when value prop changes externally
  useEffect(() => {
    if (!mapInstanceRef.current || !markerRef.current) return;
    const p = parseCoordinates(value);
    if (p) {
      markerRef.current.setLatLng([p.lat, p.lng]);
      mapInstanceRef.current.setView([p.lat, p.lng], mapInstanceRef.current.getZoom() || 14);
    }
  }, [value]);

  // Invalidate map size when switching back to map tab
  useEffect(() => {
    if (activeMode === "map" && mapInstanceRef.current) {
      setTimeout(() => {
        mapInstanceRef.current.invalidateSize();
      }, 150);
    }
  }, [activeMode]);

  // Search places via Nominatim OpenStreetMap API
  const handleSearchPlaces = async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      setSearching(true);
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          query
        )}&limit=5&addressdetails=1`,
        {
          headers: {
            "Accept-Language": "en",
          },
        }
      );
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data || []);
      }
    } catch (err) {
      console.warn("Search places failed:", err);
    } finally {
      setSearching(false);
    }
  };

  const handleSearchInputChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (val.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        handleSearchPlaces(val);
      }, 400);
    } else {
      setSearchResults([]);
    }
  };

  const handleSelectSearchResult = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    if (!isNaN(lat) && !isNaN(lng)) {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.flyTo([lat, lng], 15, { duration: 1.2 });
      }
      handleMapLocationSelect(lat, lng, false);
      if (onAddressChange && (!address || address.trim() === "")) {
        onAddressChange(result.display_name);
      }
      setSearchResults([]);
      setSearchQuery("");
    }
  };

  // Browser GPS Geolocation
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setGeoError("Geolocation is not supported by your browser.");
      return;
    }
    setGeoLocating(true);
    setGeoError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoLocating(false);
        const { latitude, longitude } = pos.coords;
        if (mapInstanceRef.current) {
          mapInstanceRef.current.flyTo([latitude, longitude], 16, { duration: 1.2 });
        }
        handleMapLocationSelect(latitude, longitude, false);
      },
      (err) => {
        setGeoLocating(false);
        setGeoError(err.message || "Unable to retrieve your location.");
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  // Auto-fill address handler
  const handleAutoFillAddress = async () => {
    const parsedCoords = parseCoordinates(value) || defaultPos;
    if (parsedCoords && parsedCoords.lat && parsedCoords.lng) {
      const detected = await fetchAddressFromCoords(parsedCoords.lat, parsedCoords.lng);
      if (detected && onAddressChange) {
        onAddressChange(detected);
      }
    }
  };

  // Copy coordinates
  const handleCopy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Clear value
  const handleClear = () => {
    if (onChange) onChange("");
  };

  const parsed = parseCoordinates(value);
  const hasValidValue = Boolean(value && value.trim() !== "" && value.trim() !== "N/A");
  const isCoordinates = Boolean(parsed && parsed.type === "coordinates");
  const isGmapsLink = Boolean(
    value &&
      (value.startsWith("http://") ||
        value.startsWith("https://") ||
        value.includes("maps.app.goo.gl") ||
        value.includes("google.com/maps"))
  );

  return (
    <div className="soloway-location-picker-card">
      {/* Header & Mode Switcher */}
      <div className="soloway-lp-header">
        <div className="soloway-lp-title-group">
          <label htmlFor={id} className="soloway-lp-label">
            <FaMapMarkerAlt className="soloway-lp-title-icon" />
            {label}
            {required && <span className="soloway-lp-required">*</span>}
          </label>
          <span className="soloway-lp-subtext">{helpText}</span>
        </div>

        {/* Mode Selector Tabs */}
        <div className="soloway-lp-tabs">
          <button
            type="button"
            className={`soloway-lp-tab ${activeMode === "map" ? "active" : ""}`}
            onClick={() => setActiveMode("map")}
            title="Pick location interactively on map"
          >
            <FaMapMarkedAlt /> Pick on Map
          </button>
          <button
            type="button"
            className={`soloway-lp-tab ${activeMode === "manual" ? "active" : ""}`}
            onClick={() => setActiveMode("manual")}
            title="Type coordinates or paste Google Maps link"
          >
            <FaKeyboard /> Manual / Link
          </button>
        </div>
      </div>

      {/* Mode 1: Interactive Map Picker */}
      <div
        className="soloway-lp-map-wrapper"
        style={{ display: activeMode === "map" ? "block" : "none" }}
      >
        {/* Map Top Toolbar: Search + GPS */}
        <div className="soloway-lp-map-toolbar">
          <div className="soloway-lp-search-box">
            <FaSearch className="soloway-lp-search-icon" />
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchInputChange}
              placeholder="Search place, city or landmark in Sri Lanka..."
              className="soloway-lp-search-input"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearchPlaces(searchQuery);
                }
              }}
            />
            {searching && (
              <span className="soloway-lp-search-spinner" title="Searching places...">
                <FaCrosshairs className="spin-icon" />
              </span>
            )}
            {searchQuery && !searching && (
              <button
                type="button"
                className="soloway-lp-search-clear"
                onClick={() => {
                  setSearchQuery("");
                  setSearchResults([]);
                }}
              >
                <FaTimes />
              </button>
            )}

            {/* Search Results Dropdown */}
            {searchResults.length > 0 && (
              <ul className="soloway-lp-search-dropdown">
                {searchResults.map((item, idx) => (
                  <li
                    key={idx}
                    className="soloway-lp-search-item"
                    onClick={() => handleSelectSearchResult(item)}
                  >
                    <FaMapMarkerAlt className="soloway-lp-item-icon" />
                    <span>{item.display_name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Quick GPS Button */}
          <button
            type="button"
            className="soloway-lp-gps-btn"
            onClick={handleUseCurrentLocation}
            disabled={geoLocating}
            title="Center map on your current location"
          >
            <FaCrosshairs className={geoLocating ? "spin-icon" : ""} />
            <span>{geoLocating ? "Locating..." : "My Location"}</span>
          </button>
        </div>

        {geoError && <div className="soloway-lp-alert warning">{geoError}</div>}

        {/* The Leaflet Map Canvas */}
        <div className="soloway-lp-map-canvas-container">
          <div ref={mapContainerRef} className="soloway-lp-leaflet-map"></div>
          <div className="soloway-lp-map-hint">
            <FaInfoCircle /> Click or drag the pin to set the exact property location
          </div>
        </div>
      </div>

      {/* Mode 2: Manual Entry / Link */}
      <div
        className="soloway-lp-manual-wrapper"
        style={{ display: activeMode === "manual" ? "block" : "none" }}
      >
        <div className="soloway-lp-input-group">
          <input
            id={id}
            type="text"
            value={value || ""}
            onChange={(e) => onChange && onChange(e.target.value)}
            placeholder={placeholder}
            required={required}
            className="soloway-lp-text-input"
          />
          {value && (
            <button
              type="button"
              className="soloway-lp-input-clear-btn"
              onClick={handleClear}
              title="Clear input"
            >
              <FaTimes />
            </button>
          )}
        </div>
        <div className="soloway-lp-manual-hints">
          <span>
            💡 <strong>Supported Formats:</strong>
          </span>
          <ul>
            <li>
              Coordinates: <code>6.9271, 79.8612</code>
            </li>
            <li>
              Google Maps link: <code>https://maps.app.goo.gl/...</code> or{" "}
              <code>https://www.google.com/maps/...</code>
            </li>
          </ul>
        </div>
      </div>

      {/* Selected Location Summary & Actions Bar */}
      {hasValidValue && (
        <div className="soloway-lp-summary-bar">
          <div className="soloway-lp-summary-info">
            <FaCheckCircle className="soloway-lp-check-icon" />
            <div className="soloway-lp-summary-text">
              <span className="soloway-lp-summary-tag">
                {isCoordinates
                  ? "Coordinates Set"
                  : isGmapsLink
                  ? "Google Maps Link Set"
                  : "Location Set"}
              </span>
              <strong className="soloway-lp-summary-val" title={value}>
                {value}
              </strong>
            </div>
          </div>

          <div className="soloway-lp-summary-actions">
            {/* Copy Button */}
            <button
              type="button"
              className="soloway-lp-action-btn"
              onClick={handleCopy}
              title="Copy location value"
            >
              <FaCopy /> {copied ? "Copied!" : "Copy"}
            </button>

            {/* Sync Address Button */}
            {onAddressChange && (
              <button
                type="button"
                className="soloway-lp-action-btn magic"
                onClick={handleAutoFillAddress}
                disabled={reverseLoading}
                title="Detect & auto-fill address from selected pin"
              >
                <FaMagic className={reverseLoading ? "spin-icon" : ""} />
                {reverseLoading ? "Detecting..." : "Auto-fill Address"}
              </button>
            )}

            {/* Open in Google Maps */}
            <a
              href={toGoogleMapsUrl(value)}
              target="_blank"
              rel="noopener noreferrer"
              className="soloway-lp-action-btn primary"
              title="Preview on Google Maps in new tab"
            >
              <FaExternalLinkAlt /> View on Google Maps
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
